// Framework-free core of the Turnstile widget handling (see src/lib/turnstile.ts for the React hook and the
// script loader). Takes the site key and the loader as arguments so it can be tested without a browser.

export interface TurnstileApi {
  render(el: HTMLElement, options: Record<string, unknown>): string;
  reset(widgetId?: string): void;
  remove(widgetId: string): void;
}

export interface TurnstileController {
  /** Render the widget into el (once the script is loaded). No-op without a site key. */
  attach(el: HTMLElement): void;
  /** Remove the widget and drop any token (the container unmounted). */
  detach(): void;
  /** The current token, waiting up to waitMs for the widget. "" when disabled, blocked or slow. Single use. */
  getToken(): Promise<string>;
}

export function createTurnstileController(
  siteKey: string,
  load: () => Promise<TurnstileApi | null>,
  waitMs = 4000,
): TurnstileController {
  let api: TurnstileApi | null = null;
  let widgetId: string | null = null;
  let token = "";
  let mounted = 0; // bumped on every attach/detach so a late script load never renders into a stale container

  return {
    attach(el) {
      if (!siteKey) return;
      const mine = ++mounted;
      void load()
        .then((a) => {
          if (!a || mine !== mounted || widgetId) return;
          api = a;
          try {
            widgetId = a.render(el, {
              sitekey: siteKey,
              appearance: "interaction-only", // visible only if Cloudflare needs the visitor to do something
              callback: (t: string) => { token = t; },
              "expired-callback": () => { token = ""; },
              "error-callback": () => { token = ""; },
            });
          } catch { /* leave the token empty; the form still submits */ }
        })
        .catch(() => { /* loader failed: leave the token empty; the form still submits */ });
    },

    detach() {
      mounted++;
      if (widgetId && api) {
        try { api.remove(widgetId); } catch { /* already gone */ }
      }
      widgetId = null;
      token = "";
    },

    async getToken() {
      if (!siteKey) return "";
      const t0 = Date.now();
      while (!token && Date.now() - t0 < waitMs) {
        await new Promise((r) => setTimeout(r, 100));
      }
      const t = token;
      token = "";
      if (widgetId && api) {
        try { api.reset(widgetId); } catch { /* ignore */ }
      }
      return t;
    },
  };
}
